import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Mail, Send, Copy, Check, ShieldCheck, ShieldOff, Clock, DollarSign,
  Loader2, ChevronRight, CheckCircle2, Circle, History, Tag
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtT = (d) => (d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const PORTAL_META = {
  not_invited: { label: 'Not Invited', color: '#64748B', desc: 'This client has not been invited to the TC portal.' },
  invitation_sent: { label: 'Invitation Sent', color: '#F59E0B', desc: 'An activation invitation has been sent.' },
  active: { label: 'Portal Active', color: '#10B981', desc: 'This client has active portal access.' },
  disabled: { label: 'Portal Disabled', color: '#EF4444', desc: 'Portal access is currently disabled.' },
};

export const PortalAccessCard = ({ customerId, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/customers/${customerId}/commercial`);
      setData(r.data.portal);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const invite = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/customers/${customerId}/invite`);
      setLink(r.data.activation_link);
      toast.success(r.data.message);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send invitation.'); }
    finally { setBusy(false); }
  };
  const toggle = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/customers/${customerId}/portal/${action}`);
      toast.success(action === 'disable' ? 'Portal access disabled' : 'Portal access re-enabled');
      await load(); onChanged && onChanged();
    } catch (e) { toast.error('Could not update portal access.'); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  if (!data) return <div className="animate-pulse bg-white/[0.05] rounded-xl h-28" />;
  const m = PORTAL_META[data.status] || PORTAL_META.not_invited;

  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="portal-access-card">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-[#C6A15B]" />
        <h4 className="text-sm font-semibold text-[#E7CB85]">Portal Access</h4>
        <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${m.color}22`, color: m.color }} data-testid="portal-status-badge">{m.label}</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">{m.desc}</p>
      {data.invitation_sent_at && data.status === 'invitation_sent' && <p className="text-[11px] text-slate-500 mb-2">Sent {fmtT(data.invitation_sent_at)}</p>}
      {data.activated_at && data.status === 'active' && <p className="text-[11px] text-slate-500 mb-2">Activated {fmtT(data.activated_at)}</p>}

      {link && (
        <div className="flex items-center gap-2 bg-black/40 rounded-lg px-2.5 py-2 mb-3">
          <span className="text-[11px] text-slate-400 truncate flex-1" data-testid="activation-link">{link}</span>
          <button onClick={copy} className="text-slate-300 hover:text-white shrink-0" data-testid="copy-activation-link">{copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {data.can_invite && data.status !== 'active' && (
          <button disabled={busy} onClick={invite} data-testid="send-invitation-btn"
            className="inline-flex items-center gap-1.5 bg-[#C6A15B] hover:bg-[#d8b36c] text-black text-xs font-medium px-3 py-1.5 rounded-lg disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {data.status === 'invitation_sent' ? 'Resend Invitation' : 'Send Portal Invitation'}
          </button>
        )}
        {data.can_toggle && data.status === 'disabled' && (
          <button disabled={busy} onClick={() => toggle('enable')} data-testid="enable-portal-btn"
            className="inline-flex items-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-medium px-3 py-1.5 rounded-lg"><ShieldCheck className="w-3.5 h-3.5" /> Re-enable Access</button>
        )}
        {data.can_toggle && ['active', 'invitation_sent'].includes(data.status) && (
          <button disabled={busy} onClick={() => toggle('disable')} data-testid="disable-portal-btn"
            className="inline-flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-medium px-3 py-1.5 rounded-lg"><ShieldOff className="w-3.5 h-3.5" /> Disable Access</button>
        )}
      </div>
    </div>
  );
};

export const SetupChecklist = ({ customerId, refreshKey }) => {
  const [setup, setSetup] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/customers/${customerId}/commercial`).then((r) => setSetup(r.data.setup)).catch(() => {});
  }, [customerId, refreshKey]);
  if (!setup) return null;
  const items = [
    { k: 'contact_info', l: 'Contact information' },
    { k: 'shipping_address', l: 'Shipping address' },
    { k: 'pricing_configured', l: 'Pricing configured' },
    { k: 'portal_invited', l: 'Portal invitation sent' },
  ];
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="setup-checklist">
      <h4 className="text-sm font-semibold text-[#E7CB85] mb-2">Account Setup</h4>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.k} className="flex items-center gap-2 text-sm">
            {setup[it.k] ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Circle className="w-4 h-4 text-slate-600" />}
            <span className={setup[it.k] ? 'text-slate-300' : 'text-slate-500'}>{it.l}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Pricing panel. Admin: full effective-price table + edit custom price. Rep: read-only tier/effective.
export const PricingPanel = ({ customerId }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/api/customers/${customerId}/pricing-detail`); setData(r.data); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const saveCustom = async (pid, clear) => {
    setBusy(true);
    try {
      const payload = clear ? { [pid]: -1 } : { [pid]: parseFloat(val) };
      // Backend merges: we must resend full map. Fetch current then patch this product.
      const cur = {};
      (data.rows || []).forEach((r) => { if (r.custom_price != null) cur[r.product_id] = r.custom_price; });
      if (clear) delete cur[pid]; else cur[pid] = parseFloat(val);
      await axios.put(`${API}/api/users/${customerId}`, { custom_prices: cur });
      toast.success(clear ? 'Custom price removed' : 'Custom price saved');
      setEditing(null); setVal(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save price.'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="animate-pulse bg-white/[0.05] rounded-xl h-28" />;
  const rows = (data.rows || []).filter((r) => !q || (r.name || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="pricing-panel">
      <div className="flex items-center gap-2 mb-3">
        <Tag className="w-4 h-4 text-[#C6A15B]" />
        <h4 className="text-sm font-semibold text-[#E7CB85]">Pricing</h4>
        <span className="ml-auto text-[11px] text-slate-400">{data.tier_name}{data.tier_discount ? ` · ${data.tier_discount}% tier` : ''}</span>
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="pricing-search"
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 mb-2 focus:border-[#C6A15B]/60 focus:outline-none" />
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-slate-500 border-b border-white/[0.06]">
            <th className="text-left font-medium py-1.5">Product</th>
            <th className="text-right font-medium py-1.5">Base</th>
            <th className="text-right font-medium py-1.5 hidden sm:table-cell">Tier</th>
            <th className="text-right font-medium py-1.5">Custom</th>
            <th className="text-right font-medium py-1.5">Effective</th>
            {data.can_manage && <th className="py-1.5"></th>}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.product_id} className="border-b border-white/[0.03]" data-testid={`pricing-row-${r.product_id}`}>
                <td className="py-2 text-slate-200 pr-2">{r.name}<div className="text-[10px] text-slate-500">{r.source}</div></td>
                <td className="py-2 text-right text-slate-400">{money(r.base_price)}</td>
                <td className="py-2 text-right text-slate-400 hidden sm:table-cell">{money(r.tier_price)}</td>
                <td className="py-2 text-right">
                  {editing === r.product_id ? (
                    <input autoFocus type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`pricing-input-${r.product_id}`}
                      className="w-20 bg-black/50 border border-[#C6A15B]/50 rounded px-2 py-1 text-right text-slate-100" />
                  ) : (r.custom_price != null ? <span className="text-[#C6A15B]">{money(r.custom_price)}</span> : <span className="text-slate-600">—</span>)}
                </td>
                <td className="py-2 text-right text-white font-medium">{money(r.effective_price)}</td>
                {data.can_manage && (
                  <td className="py-2 text-right whitespace-nowrap">
                    {editing === r.product_id ? (
                      <span className="flex gap-1 justify-end">
                        <button disabled={busy} onClick={() => saveCustom(r.product_id, false)} className="text-emerald-400" data-testid={`pricing-save-${r.product_id}`}><Check className="w-4 h-4" /></button>
                        <button onClick={() => { setEditing(null); setVal(''); }} className="text-slate-500">✕</button>
                      </span>
                    ) : (
                      <span className="flex gap-2 justify-end">
                        <button onClick={() => { setEditing(r.product_id); setVal(r.custom_price ?? ''); }} className="text-slate-400 hover:text-white" data-testid={`pricing-edit-${r.product_id}`}>Edit</button>
                        {r.custom_price != null && <button onClick={() => saveCustom(r.product_id, true)} className="text-red-400" data-testid={`pricing-clear-${r.product_id}`}>Clear</button>}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const CustomerActivity = ({ customerId, refreshKey }) => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/customers/${customerId}/activity`).then((r) => setEvents(r.data.activity || [])).catch(() => {});
  }, [customerId, refreshKey]);
  if (events.length === 0) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="customer-activity">
      <div className="flex items-center gap-2 mb-3"><History className="w-4 h-4 text-[#C6A15B]" /><h4 className="text-sm font-semibold text-[#E7CB85]">Account Activity</h4></div>
      <ol className="space-y-2.5">
        {events.map((e) => (
          <li key={e.activity_id} className="relative pl-4 border-l border-white/10">
            <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-[#C6A15B]" />
            <p className="text-slate-200 text-sm">{e.label}</p>
            <p className="text-[11px] text-slate-500">{fmtT(e.timestamp)}{e.performed_by_name ? ` · ${e.performed_by_name}` : ''}</p>
          </li>
        ))}
      </ol>
    </div>
  );
};
